/* eslint-disable arrow-body-style */

import { LoadingOverlay } from "@src/common/LoadingOverlay";
import { PROD_REFERRAL_WEBFORM_URL, STAGE_REFERRAL_WEBFORM_URL } from "@src/constants";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useEnvironment } from "@src/context/EnvironmentContext";
import { useInteractionGuard } from "@src/context/InteractionGuardContext";
import { useThemeContext } from "@src/context/ThemeContext";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import ClientLogsService from "@src/services/client-logs.service";
import type { Partner } from "@src/services/partners.service";
import { fetchPartners } from "@src/services/partners.service";
import { upsertReferrer } from "@src/services/referrer.service";
import { fetchReferrerTeams } from "@src/services/referrer-teams.service";
import { getThemeNameFromTag } from "@src/themes";
import { navigateToContactUs } from "@src/utils/contact-us-navigation";
import logger from "@src/utils/logger";
import { useLocation, useParams } from "@uhg-abyss/web/tools/reactRouterTools";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Card } from "@uhg-abyss/web/ui/Card";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { Fragment, type SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import { handleReferScheduleNowSuccess } from "./refer-success-navigation";
import { markReferThemeChangeInProgress } from "./themeChangeSignal";

const initialFormState = (data: any) => ({
  partners: [] as Partner[],
  referrerTeams: [] as any[],
  selectedPartner: data.selectedPartner || "",
  selectedReferrerTeam: data.selectedReferrerTeam || "",
});

type ReferFormState = ReturnType<typeof initialFormState>;

const getSelectedPartnerValue = (
  previousState: ReferFormState,
  singlePartnerId: string,
  shouldAutoSelectPartner: boolean,
  shouldClearSelection: boolean,
) => {
  if (shouldAutoSelectPartner) return singlePartnerId;
  if (shouldClearSelection) return "";
  return previousState.selectedPartner;
};

const getUpdatedFormStateForPartners = (
  previousState: ReferFormState,
  partners: Partner[],
): ReferFormState => {
  const singlePartnerId = partners.length === 1 ? partners[0].id : "";
  const shouldAutoSelectPartner = singlePartnerId !== "";
  const hasSelectedPartnerChanged =
    shouldAutoSelectPartner && previousState.selectedPartner !== singlePartnerId;

  const isPreviousPartnerStillValid =
    previousState.selectedPartner !== "" &&
    partners.some((partner) => partner.id === previousState.selectedPartner);

  const shouldClearSelection = !shouldAutoSelectPartner && !isPreviousPartnerStillValid;
  const shouldResetReferrerState = hasSelectedPartnerChanged || shouldClearSelection;

  return {
    ...previousState,
    partners,
    selectedPartner: getSelectedPartnerValue(
      previousState,
      singlePartnerId,
      shouldAutoSelectPartner,
      shouldClearSelection,
    ),
    referrerTeams: shouldResetReferrerState ? [] : previousState.referrerTeams,
    selectedReferrerTeam: shouldResetReferrerState ? "" : previousState.selectedReferrerTeam,
  };
};

interface ReferFormLegacyProps {
  isMobile: boolean;
}

export const ReferFormLegacy = ({ isMobile }: ReferFormLegacyProps) => {
  const { data, markRouteAsComplete, setData } = useApplicationContext();
  const { APP_ENV } = useEnvironment();
  const { activeThemeName, setBranding } = useThemeContext();
  const { parentOrganization } = useParams();
  const location = useLocation();
  const [formState, setFormState] = useState(initialFormState(data));
  const [errors, setErrors] = useState({
    selectedPartner: "",
    selectedReferrerTeam: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { runExclusive } = useInteractionGuard();
  const [referrerTeamsLoadedForPartner, setReferrerTeamsLoadedForPartner] = useState("");
  const selectedPartnerRef = useRef(formState.selectedPartner);
  const navigate = useNavigateWithUTM();

  useEffect(() => {
    selectedPartnerRef.current = formState.selectedPartner;
  }, [formState.selectedPartner]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setIsRedirecting(false);
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useEffect(() => {
    const loadPartners = async () => {
      if (formState.partners.length > 0 || errors.selectedPartner !== "" || isRedirecting) return;

      setIsLoading(true);
      const partners = await fetchPartners(parentOrganization, data.userUuid);

      if (!Array.isArray(partners)) {
        setErrors((previousErrors) => ({
          ...previousErrors,
          selectedPartner: "Failed to load partners. Please try refreshing the page.",
        }));
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "ReferFormLegacy.loadPartners",
          "Failed to load partners.",
          new Error(partners.error),
        );
        setIsLoading(false);
        return;
      }

      setFormState((previousState) => getUpdatedFormStateForPartners(previousState, partners));

      if (partners.length === 1) {
        setReferrerTeamsLoadedForPartner("");
        setErrors((previousErrors) => ({
          ...previousErrors,
          selectedPartner: "",
          selectedReferrerTeam: "",
        }));
      }

      setIsLoading(false);
    };

    loadPartners();
  }, [isRedirecting, parentOrganization]);

  useEffect(() => {
    const loadReferrerTeams = async () => {
      if (
        formState.selectedPartner === "" ||
        referrerTeamsLoadedForPartner === formState.selectedPartner ||
        errors.selectedReferrerTeam !== "" ||
        isRedirecting
      )
        return;

      setIsLoading(true);
      const selectedPartner = formState.selectedPartner;
      const referrerTeams = await fetchReferrerTeams(selectedPartner, data.userUuid || undefined);

      if (selectedPartnerRef.current !== selectedPartner) {
        setIsLoading(false);
        return;
      }

      if (referrerTeams.error) {
        setErrors((previousErrors) => ({
          ...previousErrors,
          selectedReferrerTeam: "Failed to load referrer teams. Please try refreshing the page.",
        }));
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "ReferFormLegacy.loadReferrerTeams",
          "Failed to load referrer teams.",
          new Error(referrerTeams.error),
        );
        setIsLoading(false);
        return;
      }

      setFormState((previousState) => {
        if (previousState.selectedPartner !== selectedPartner) return previousState;

        const singleReferrerTeamId = referrerTeams.length === 1 ? referrerTeams[0].id : "";
        return {
          ...previousState,
          referrerTeams,
          selectedReferrerTeam: singleReferrerTeamId || previousState.selectedReferrerTeam,
        };
      });
      setReferrerTeamsLoadedForPartner(selectedPartner);

      if (referrerTeams.length === 1) {
        setErrors((previousErrors) => ({ ...previousErrors, selectedReferrerTeam: "" }));
      }

      setIsLoading(false);
    };

    loadReferrerTeams();
  }, [
    formState.selectedPartner,
    referrerTeamsLoadedForPartner,
    errors.selectedReferrerTeam,
    isRedirecting,
  ]);

  useEffect(() => {
    if (formState.selectedPartner && !data.hasReferFormInput) {
      setData("hasReferFormInput", true);
    }
  }, [data.hasReferFormInput, formState.selectedPartner, setData]);

  useEffect(() => {
    setData("selectedPartner", formState.selectedPartner);
    setData("selectedReferrerTeam", formState.selectedReferrerTeam);
  }, [formState.selectedPartner, formState.selectedReferrerTeam, setData]);

  useEffect(() => {
    if (formState.partners.length === 0) return;

    const selectedPartner = formState.partners.find(
      (partner) => partner.id === formState.selectedPartner,
    );

    if (getThemeNameFromTag(selectedPartner?.branding) !== activeThemeName) {
      ClientLogsService.logWebAction(
        data.userUuid || "unknown",
        "ReferFormLegacy.useEffect[activeThemeName, formState.partners, formState.selectedPartner]",
        `Partner ${selectedPartner?.name} has different branding than active theme. Triggering theme change.`,
      );
      markReferThemeChangeInProgress();
    }

    setBranding(selectedPartner?.branding);
  }, [activeThemeName, formState.partners, formState.selectedPartner, setBranding]);

  const ReferFormWrapper = useMemo(
    () =>
      styled("form", {
        width: "100%",
        maxWidth: "100%",
      }),
    [],
  );

  const FormSpacing = useMemo(
    () =>
      styled("div", {
        display: "flex",
        flexDirection: "column",
        gap: "$core.spacing.300",
      }),
    [],
  );

  const validateField = (field: keyof typeof formState, fieldValue: string) => {
    switch (field) {
      case "selectedPartner":
        if (fieldValue === "") return "Please select a partner";
        break;
      case "selectedReferrerTeam":
        if (fieldValue === "") return "Please select a referrer team";
        break;
      default:
        return null;
    }
  };

  const validateForm = () => {
    const newErrors = { selectedPartner: "", selectedReferrerTeam: "" };
    const partnerError = validateField("selectedPartner", formState.selectedPartner);
    const teamError = validateField("selectedReferrerTeam", formState.selectedReferrerTeam);
    if (partnerError) newErrors.selectedPartner = partnerError;
    if (teamError) newErrors.selectedReferrerTeam = teamError;
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  const handleChange = (field: keyof typeof formState, newFieldValue: string) => {
    if (field === "selectedPartner") {
      selectedPartnerRef.current = newFieldValue;
      setReferrerTeamsLoadedForPartner("");
      setFormState((previousState) => ({
        ...previousState,
        [field]: newFieldValue,
        referrerTeams: [],
        selectedReferrerTeam: "",
      }));
    } else {
      setFormState((previousState) => ({ ...previousState, [field]: newFieldValue }));
    }
    setErrors({ selectedPartner: "", selectedReferrerTeam: "" });
  };

  const handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    const isFormValid = validateForm();

    if (isFormValid) {
      ClientLogsService.logWebAction(
        data.userUuid || "unknown",
        "ReferFormLegacy.handleSubmit",
        "Form validation passed, submitting form.",
        undefined,
        {
          text: `Refer form submitted. Partner: ${formState.selectedPartner}. Referrer team: ${formState.selectedReferrerTeam}.`,
        },
      );
      if (
        ((event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement)?.name === "scheduleNow"
      ) {
        await runExclusive("refer:schedule-now", async () => {
          const response = await submitReferrer({ requestedCallback: false });

          if (response.error) {
            ClientLogsService.logError(
              data.userUuid || "unknown",
              "ReferFormLegacy.handleSubmit",
              "Failed to submit referrer information for 'Schedule Now' scenario - Redirecting to Contact Us",
              new Error(response.error),
            );
            logger.error({ error: response.error });
            setIsLoading(false);
            navigateToContactUs(navigate, data.userUuid, undefined, false);
          } else {
            ClientLogsService.logWebAction(
              data.userUuid || "unknown",
              "ReferFormLegacy.handleSubmit",
              "Successfully submitted referrer information for 'Schedule Now' scenario - Redirecting Get Started.",
            );
            handleReferScheduleNowSuccess(
              formState.partners,
              formState.selectedPartner,
              setData,
              markRouteAsComplete,
              setIsLoading,
              navigate,
              location,
            );
          }
        });
      } else {
        await runExclusive("refer:request-callback", async () => {
          await submitReferrer({ requestedCallback: true });
          setIsRedirecting(true);
          ClientLogsService.logWebAction(
            data.userUuid || "unknown",
            "ReferFormLegacy.handleSubmit",
            "Submitted referrer information for 'Request a callback' scenario, redirecting to referral webform.",
          );
        });
        globalThis.location.href = referralWebformUrl();
      }
    } else {
      ClientLogsService.logUserError(
        data.userUuid || "unknown",
        "ReferFormLegacy.handleSubmit",
        "Form has validation errors, unable to proceed",
      );
    }

    setIsLoading(false);
  };

  const submitReferrer = async ({ requestedCallback }: { requestedCallback: boolean }) => {
    return await upsertReferrer({
      user_uuid: data.userUuid,
      role: "referrer",
      partner_id: formState.selectedPartner,
      referrer_team_id: formState.selectedReferrerTeam,
      requested_callback: requestedCallback,
    });
  };

  const referralWebformUrl = () => {
    const isProd = APP_ENV === "prod";
    const url = isProd ? PROD_REFERRAL_WEBFORM_URL : STAGE_REFERRAL_WEBFORM_URL;
    const queryParams = new URLSearchParams(globalThis.location.search);

    if (formState.selectedPartner) {
      const selectedPartner = formState.partners.find(
        (partner) => partner.id === formState.selectedPartner,
      );
      if (selectedPartner) {
        queryParams.append("partnerId", selectedPartner.id);
        queryParams.append("partnerName", selectedPartner.name);
      }
    }

    if (formState.selectedReferrerTeam) {
      const selectedReferrerTeam = formState.referrerTeams.find(
        (team) => team.id === formState.selectedReferrerTeam,
      );
      if (selectedReferrerTeam) {
        queryParams.append("referrerTeamId", selectedReferrerTeam.id);
        queryParams.append("referrerTeamName", selectedReferrerTeam.name);
      }
    }

    const queryString = queryParams.toString();
    return queryString ? `${url}?${queryString}` : url;
  };

  const partnerOptions = useMemo(
    () =>
      formState.partners
        .filter((partner) => partner.name !== "Referral Webform")
        .map((partner) => ({ value: partner.id, label: partner.name })),
    [formState.partners],
  );

  const referrerTeamOptions = useMemo(
    () =>
      formState.referrerTeams
        .filter((referrerTeam) => referrerTeam.name !== "General Referral Team")
        .map((referrerTeam) => ({
          value: referrerTeam.id,
          label: referrerTeam.name,
        })),
    [formState.referrerTeams],
  );

  const isSinglePartnerOption = formState.partners.length === 1;
  const isSingleReferrerTeamOption = formState.referrerTeams.length === 1;
  const shouldShowReferrerTeamInput =
    isRedirecting ||
    (formState.selectedPartner !== "" &&
      (referrerTeamsLoadedForPartner === formState.selectedPartner ||
        errors.selectedReferrerTeam !== ""));

  return (
    <Fragment>
      <LoadingOverlay isLoading={isLoading} />
      <ReferFormWrapper id="refer-form" onSubmit={(event) => handleSubmit(event)}>
        <Card
          className={isMobile ? "v2card-form-mobile-style" : ""}
          css={{ padding: isMobile ? "20px" : "2rem", minHeight: isMobile ? "145px" : "auto" }}
        >
          <FormSpacing>
            <SelectInput
              label="Partner"
              value={formState.selectedPartner}
              onChange={(value) => handleChange("selectedPartner", value as string)}
              isRequired
              isSearchable={true}
              isDisabled={isSinglePartnerOption}
              options={partnerOptions}
              errorMessage={errors.selectedPartner}
              data-testid="test-id-refer-partner-select"
            />
            {shouldShowReferrerTeamInput && (
              <SelectInput
                label="Referrer Team"
                value={formState.selectedReferrerTeam}
                onChange={(value) => handleChange("selectedReferrerTeam", value as string)}
                isRequired
                isSearchable={true}
                isDisabled={isSingleReferrerTeamOption}
                options={referrerTeamOptions}
                errorMessage={errors.selectedReferrerTeam}
                data-testid="test-id-refer-referrer-team-select"
              />
            )}
          </FormSpacing>
        </Card>
      </ReferFormWrapper>
    </Fragment>
  );
};
